/**
 * How a finished run reads once a later run of the same task has succeeded.
 *
 * The stored summary stays as Brunel wrote it. This only decides what the
 * Recent runs list says: an earlier failed or timed-out run is labelled with
 * the later success, and the sentence that tells the owner to send the task
 * again is left off that row. A different task with the same title is not
 * the same task.
 */

export interface SupersessionRun {
  readonly assignment_id: string;
  readonly project_id: string;
  readonly task_id: string;
  readonly success: boolean;
  readonly reported_at: string;
  readonly summary: string;
  readonly worker_number: number | null;
}

export interface RecentRunView {
  readonly assignmentId: string;
  readonly superseded: boolean;
  /** "superseded by Worker 5 · run 5674d5e0", or null when this run still stands. */
  readonly supersededByLabel: string | null;
  /** The failure record, with send-again guidance removed only when superseded. */
  readonly summary: string;
  readonly retryGuidance: boolean;
}

/**
 * The three sentences Brunel appends to a failed or timed-out run. Other
 * "send it again" wording (owner-command checks, project choice) is not this
 * guidance and stays in the record.
 */
const GUIDANCE_SOURCES = [
  String.raw`,?\s*then send the task again\.?`,
  String.raw`;\s*send the task again\.?`,
  String.raw`;\s*send it again, in smaller steps if it is a long job\.?`,
] as const;

function reportedMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/** Project plus task id. A blank id matches nothing, including another blank id. */
function taskKey(run: Pick<SupersessionRun, 'project_id' | 'task_id'>): string | null {
  const projectId = run.project_id.trim();
  const taskId = run.task_id.trim();
  if (projectId === '' || taskId === '') return null;
  return `${projectId}\n${taskId}`;
}

/** Whether this summary still tells the owner to send the task again. */
export function showsRetryGuidance(summary: string): boolean {
  return GUIDANCE_SOURCES.some((source) => new RegExp(source, 'i').test(summary));
}

/**
 * The failure record without the clause that says to send the task again.
 * The rest of the summary, including a timeout or a lost computer, stays.
 */
export function withoutRetryGuidance(summary: string): string {
  let text = summary;
  for (const source of GUIDANCE_SOURCES) text = text.replace(new RegExp(source, 'gi'), '');
  const flat = text.replace(/[ \t]+/g, ' ').replace(/\s+([.;])/g, '$1').trim();
  if (flat === '') return '';
  return /[.!?]$/.test(flat) ? flat : `${flat}.`;
}

function supersededByLabel(run: SupersessionRun): string {
  const runId = run.assignment_id.slice(0, 8);
  const who = run.worker_number === null ? `run ${runId}` : `Worker ${run.worker_number} · run ${runId}`;
  return `superseded by ${who}`;
}

/**
 * One view per assignment. A failed or timed-out run is superseded only by a
 * later successful run of the same project and task. Title is never consulted.
 */
export function presentRecentRuns(runs: readonly SupersessionRun[]): ReadonlyMap<string, RecentRunView> {
  const latestSuccess = new Map<string, SupersessionRun>();
  for (const run of runs) {
    if (!run.success) continue;
    const key = taskKey(run);
    if (key === null) continue;
    const current = latestSuccess.get(key);
    if (current === undefined || reportedMs(run.reported_at) > reportedMs(current.reported_at)) {
      latestSuccess.set(key, run);
    }
  }

  const views = new Map<string, RecentRunView>();
  for (const run of runs) {
    const key = taskKey(run);
    const later = run.success || key === null ? undefined : latestSuccess.get(key);
    const superseded = later !== undefined && reportedMs(later.reported_at) > reportedMs(run.reported_at);
    const guidance = showsRetryGuidance(run.summary);
    views.set(run.assignment_id, {
      assignmentId: run.assignment_id,
      superseded,
      supersededByLabel: superseded && later !== undefined ? supersededByLabel(later) : null,
      summary: superseded ? withoutRetryGuidance(run.summary) : run.summary,
      retryGuidance: superseded ? false : guidance,
    });
  }
  return views;
}
